import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GatherApiService } from '../gather-services/gather-api.service';
import { SharedDataService } from '../gather-services/shared-data.service';
import { GatherStateService } from '../gather-services/gather-state.service';
import {
  Availability,
  Gather,
  JobPosting,
  PersonPortfolioUrl
} from "../gather-typings";
import { Subscription} from "rxjs";
import { UntypedFormBuilder, UntypedFormGroup, FormControl } from "@angular/forms";
import { PreInterviewQuestionsService } from "../../pre-interview-questions/pre-interview-questions.service";
import { FeatureService, TalentAppFeatures } from "../../service/feature.service";
import { Country } from "../enums";
import { TalentHappyPathDataService } from 'src/app/talent-happy-path/shared/service/talent-happy-path-data-service';
import { Countries, FileUploadUserType, TalentProfilePage } from 'src/app/enums';
import { urlValidator } from '../../talent-happy-path/shared/validators/urlValidator';
import { MatDialog } from '@angular/material/dialog';
import { NotAFitModalComponent } from "./modal/not-a-fit-modal.component";
import { PageService } from "../../talent-happy-path/talent-profile/shared/page-service";
import { LocationPickerComponent } from "../../talent-happy-path/shared/components/location-picker/location-picker.component";
import { GatherHomeLocationService } from "../gather-services/gather-home-location.service";

const NOT_FOUND_RESPONSE: number[] = [404, 408, 421, 422];
const NOT_ACCESSIBLE_RESPONSE: number[] = [407, 423];
@Component({
  selector: 'app-gather-not-interested',
  templateUrl: './gather-not-interested.component.html',
  styleUrls: ['./gather-not-interested.component.scss'],
  standalone: false,
  providers: [GatherHomeLocationService]
})
export class GatherNotInterestedComponent implements OnInit {
  @ViewChild(LocationPickerComponent)
  set locationPicker(locationPicker: LocationPickerComponent) {
    this.currentLocationPicker = locationPicker;
    this.homeLocationService.prefill(locationPicker, this.form);
  }
  private currentLocationPicker: LocationPickerComponent = null;
  activePage: number = 0;
  trackingId: string = null;
  jobPosting: JobPosting = null;
  gather: Gather = null;
  talent: Talent = null;
  availability: Availability = {};
  fromTextMessage: boolean = false;
  private subscriptions = new Subscription();
  form: UntypedFormGroup = this.formBuilder.group(
    {
      notes: '',
      resumeUpload: null,
      portfolioUrl_0: new FormControl('', [urlValidator()]),
      permissionToSubmit: null,
    }
  );
  loading = true;
  hasPreInterviewQuestions: boolean = false;
  preInterviewFeature = false;
  isJapanOrder: boolean;
  talentPortfolioURLs: PersonPortfolioUrl[] = [];
  newPortfolioUrl: PersonPortfolioUrl = { personUriId: undefined, personId: undefined, uri: '' };
  showPortfolioLinks = true;
  errorMessage: string='';
  resumeUploaded: boolean = false;
  resumeUploading: boolean = false;
  fileUploadUserType: FileUploadUserType = FileUploadUserType.GATHER;
  availabilityDate: Date;
  payTransparencyFeature = false;
  totalPages = 5;

  constructor(private router: Router,
              private route: ActivatedRoute,
              private pageService: PageService,
              private gatherApiService: GatherApiService,
              private sharedDataService: SharedDataService,
              private gatherStateService: GatherStateService,
              private preInterviewQuestionService: PreInterviewQuestionsService,
              private formBuilder: UntypedFormBuilder,
              private featureService: FeatureService,
              private talentHappyPathDataService: TalentHappyPathDataService,
              private homeLocationService: GatherHomeLocationService,
              public dialog: MatDialog) {
    this.preInterviewFeature = this.featureService.getFeature(TalentAppFeatures.PRE_INTERVIEW_QUESTIONS)
    this.payTransparencyFeature = this.featureService.getFeature(TalentAppFeatures.APPLICANT_PAY_TRANSPARENCY_FEATURE)
  }

  ngOnInit(): void {
    this.fromTextMessage =
      this.route.snapshot.queryParamMap.has('textMessage') && this.route.snapshot.queryParamMap.get('textMessage') === 'true';

    this.subscriptions.add(
      this.sharedDataService.trackingId.subscribe(id => {
        this.trackingId = id;
      })
    );

    this.subscriptions.add(
      this.sharedDataService.jobPosting.subscribe(jobPosting => {
        this.jobPosting = jobPosting;
        this.isJapanOrder = jobPosting.countryId === Country.Japan;
        if (this.jobPosting.orderId != -1) {
          this.subscriptions.add(
            this.preInterviewQuestionService.getSelectedQuestions(this.jobPosting.orderId).subscribe(selectedQuestions => {
              const activeQuestions = selectedQuestions.filter(question => question.isActive)
              this.hasPreInterviewQuestions = activeQuestions.length > 0;
            })
          );
        }

        this.talentHappyPathDataService.postingInfo = {
          jobPostingId: this.jobPosting.jobPostingId,
          jobTitle: this.jobPosting.jobTitle,
          jobDescription: this.jobPosting.jobDescription,
          orderId: this.jobPosting.orderId,
          marketId: this.jobPosting.marketId,
          countryId: this.jobPosting.countryId,
          isPosted: true // hardcoding because it is not used
        };
      })
    );

    this.subscriptions.add(
      this.sharedDataService.gather.subscribe(gather => {
        this.gather = gather;
        this.talentHappyPathDataService.gather = gather;
        if (gather.talentHomeMarketCountryId === Countries.USA || gather.talentHomeMarketCountryId === Countries.AU) {
            this.totalPages = 4;
        }
        this.talentHappyPathDataService.homeMarketCountryId = gather.talentHomeMarketCountryId;
        this.talentHappyPathDataService.talent = {
          email: gather.talentEmail,
          personId:gather.talentPersonId,
          firstName: gather.talent?.firstName,
          lastName: gather.talent?.lastName,
          preferredName: gather.talent?.preferredName,
          personTypeId: undefined,
          profileUpdated: undefined,
          resumeUpdated: undefined,
          resumeData: undefined,
          termsAccepted: undefined,
          locationUpdated: undefined,
          smsOptIn: undefined,
          doubleOptIn: undefined,
          newTHPTalent: undefined,
          geoLocation: undefined,
          portfolioUpdated: undefined,
          countryId: gather.talentHomeMarketCountryId
        };

        if (gather.talent) {
          this.talent = {
            email: gather.talentEmail,
            personId: gather.talentPersonId,
            firstName: gather.talent.firstName,
            lastName: gather.talent.lastName,
            preferredName: gather.talent.preferredName,
            phoneNumber: gather.talentPhoneNumber,
            personTypeId: undefined,
            profileUpdated: undefined,
            resumeUpdated: undefined,
            resumeData: undefined,
            termsAccepted: undefined,
            locationUpdated: undefined,
            smsOptIn: undefined,
            doubleOptIn: undefined,
            newTHPTalent: undefined,
            geoLocation: undefined,
            portfolioUpdated: undefined,
            countryId: gather.talentHomeMarketCountryId
          };
        }

        if (this.gather.talentPersonId != -1) {
          this.gatherApiService.getTalentPersonProfileUrls(this.gather.talentPersonId).subscribe(
            portfolioUrls => {
              if (portfolioUrls.length > 0) {
                this.talentPortfolioURLs = portfolioUrls;
              }
              this.initializePortfolioUrlControls();
            }
          );

          this.subscriptions.add(
            this.homeLocationService.load(this.trackingId).subscribe(() => {
              this.homeLocationService.prefill(this.currentLocationPicker, this.form);
            })
          );
        }
      })
    );

    this.pageService.activePage$.subscribe(page => {
      this.activePage = page;
    });

    this.gatherStateService.setTitle('gather.interested.title');

    this.showDialog()
  }

  initializePortfolioUrlControls(): void {
    for (let i = 0; i < this.talentPortfolioURLs.length; i++) {
      if (i > 0) {
        this.form.addControl(`portfolioUrl_${i}`, new FormControl('', [urlValidator()]));
      }

      this.form.get(`portfolioUrl_${i}`).setValue(this.talentPortfolioURLs[i].uri);
    }
  }

  resetResponseCodes() {
    for (let portfolioUrl of this.talentPortfolioURLs) {
      portfolioUrl.responseCode = undefined;
    }

    this.newPortfolioUrl.responseCode = undefined;
  }

  validateAndUpdateURL(portfolioURL: PersonPortfolioUrl, event): void {
    portfolioURL.uri = event.target.value;

    if (portfolioURL.uri) {
      /* Do not call the backend to verify an empty string as a URL */
      this.gatherApiService.verifyPersonUri(portfolioURL.uri).subscribe(
        response => {
          if (response === 0) {
            portfolioURL.responseCode = 0;
          } else if (NOT_FOUND_RESPONSE.includes(response)) {
            portfolioURL.responseCode = 404;
          } else if (NOT_ACCESSIBLE_RESPONSE.includes(response)) {
            portfolioURL.responseCode = 403;
          } else {
            portfolioURL.responseCode = 500;
          }

          if (response === 0) {
            /* Only save valid URLs */
            this.gatherApiService.updatePersonURI(portfolioURL).subscribe();
          } else {
            for (let i = 0; i < this.talentPortfolioURLs.length; i++) {
              /* Make sure the FormControl URL formatting validation takes precedence */
              if (this.form.get(`portfolioUrl_${i}`).value === portfolioURL.uri) {
                if (this.form.get(`portfolioUrl_${i}`).invalid) {
                  this.talentPortfolioURLs[i].responseCode = 0;
                } else {
                  /* Show error when URL cannot be accessed */
                  this.form.get(`portfolioUrl_${i}`).setErrors({'incorrect': true});
                }
              }
            }
          }
        });
    }
  }

  validateAndSaveURL(portfolioURL: PersonPortfolioUrl, event): void {
    if (!portfolioURL || !portfolioURL.uri) {
      portfolioURL = {personId: this.gather.talentPersonId, personUriId: 0, uri: ''}
      portfolioURL.uri = event.target.value;
    }
    if (portfolioURL.uri) {
      /* Do not call the backend to verify an empty string as a URL */
      this.gatherApiService.verifyPersonUri(portfolioURL.uri).subscribe(
        response => {
          if (response === 0) {
            portfolioURL.responseCode = 0;
          } else if (NOT_FOUND_RESPONSE.includes(response)) {
            portfolioURL.responseCode = 404;
          } else if (NOT_ACCESSIBLE_RESPONSE.includes(response)) {
            portfolioURL.responseCode = 403;
          } else {
            portfolioURL.responseCode = 500;
          }

          this.newPortfolioUrl.responseCode = portfolioURL.responseCode;

          if (response === 0) {
            /* Only save valid URLs */
            this.savePortfolioURL(portfolioURL);
          } else {
            /* Make sure the FormControl URL formatting validation takes precedence */
            if (this.form.get('portfolioUrl_0').invalid) {
              this.newPortfolioUrl.responseCode = 0;
            } else {
              /* Show error when URL cannot be accessed */
              this.form.get('portfolioUrl_0').setErrors({'incorrect': true});
            }
          }
        });
    }
  }

  savePortfolioURL(portfolioURL: PersonPortfolioUrl): void {
    portfolioURL.personId = this.gather.talentPersonId;
    this.gatherApiService.savePersonURI(portfolioURL).subscribe(
      response => {
        portfolioURL.personUriId = response;
        this.talentPortfolioURLs.push(portfolioURL);
        this.newPortfolioUrl = { personUriId: undefined, personId: undefined, uri: '' };
      }
    );
  }

  deletePortfolioURL(portfolioURL: PersonPortfolioUrl): void {
    this.gatherApiService.deletePersonUri(portfolioURL.personUriId).subscribe();
    this.talentPortfolioURLs = this.talentPortfolioURLs.filter( el => el.personUriId !== portfolioURL.personUriId);

    if (this.talentPortfolioURLs.length === 0) {
      this.form.get('portfolioUrl_0').setValue('');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  resumeUploadedHandler(event: boolean): void {
    this.resumeUploaded = event;
  }

  resumeUploadingHandler(event: boolean): void {
    this.resumeUploading = event;
  }

  showDialog(): void {
    if (this.router.url.includes('not-interested')) {
      this.dialog.open(NotAFitModalComponent, {
        disableClose: true
      });
    }
  }

  goToEmploymentOpportunities() {
    // Wait for the home location: advancing hides this page, and with it the error, so a failed save has to be
    // reported while the talent can still see the field. Pressing Next again retries.
    this.subscriptions.add(
      this.homeLocationService.save(this.trackingId, this.gather.talentPersonId, this.form).subscribe(
        () => this.continueToEmploymentOpportunities(),
        error => {
          this.errorMessage = 'gather.availability.error';
          console.error('Error saving home location: ' + error.code);
        })
    );
  }

  private continueToEmploymentOpportunities(): void {
    if (this.talentHappyPathDataService.resumeDetail) {
      this.talentHappyPathDataService.talent.resumeUpdated = this.resumeUploaded;
      this.talentHappyPathDataService.resumeDetail.personId = this.gather.talentPersonId;
      this.gatherApiService.saveResumeDetail(this.talentHappyPathDataService.resumeDetail).subscribe(
        () => {
          console.log('Updated resume saved successfully');
        },
        error => {
          this.loading = false;
          this.errorMessage = 'gather.availability.error';
          console.error('Error saving updated resume: ' + error.code);
        });
    }
    this.pageService.setActivePage(TalentProfilePage.EMPLOYMENT_OPPORTUNITIES)
  }

  getActivePageNumber() {
    return this.activePage;
  }

  getCompletionPercentage() {
    return (this.activePage / this.totalPages) * 100;
  }

  protected readonly TalentProfilePage = TalentProfilePage;
}
